import { useEffect, useRef, useState } from 'react'

import { wsService } from '@/services/websocket'

export interface LiveTick {
  symbol: string
  price: number
  timestamp: number
}

/**
 * Subscribes to live tick updates for the given symbol via the backend WebSocket.
 * Returns the latest tick (null until first tick arrives).
 *
 * The WS connection and subscription are cleaned up automatically on unmount or
 * when the symbol changes.
 */
export function useLiveTick(symbol: string): LiveTick | null {
  const [tick, setTick] = useState<LiveTick | null>(null)
  // Keep a stable ref to avoid stale closure in the WS handler
  const symbolRef = useRef(symbol)
  symbolRef.current = symbol

  useEffect(() => {
    if (!symbol) return

    wsService.connect()
    wsService.subscribe([symbol], ['tick'])

    const unsubscribe = wsService.on((msg) => {
      if (msg.type === 'tick' && msg.symbol === symbolRef.current) {
        setTick({
          symbol: msg.symbol,
          price: msg.data.price,
          timestamp: msg.data.timestamp,
        })
      }
    })

    return () => {
      unsubscribe()
      wsService.unsubscribe([symbol])
    }
  }, [symbol])

  // Reset tick when symbol changes so stale prices aren't shown
  useEffect(() => {
    setTick(null)
  }, [symbol])

  return tick
}
